import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { Luggage } from '@mui/icons-material';

/**
 * Reusable Flight Summary Card component for displaying flight details
 * Used in both Fare Selection and Checkout pages
 * Compact Duffel-style layout
 */
function FlightSummaryCard({ 
  outboundFlight, 
  inboundFlight = null, 
  totalPrice = null,
  showPrice = false
}) {
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Calculate day difference between departure and arrival
  const getDayDifference = (departureDate, arrivalDate) => {
    if (!departureDate || !arrivalDate) return 0;
    const dep = new Date(departureDate);
    const arr = new Date(arrivalDate);
    
    // Set time to midnight for both dates to compare only the date part
    const depDay = new Date(dep.getFullYear(), dep.getMonth(), dep.getDate());
    const arrDay = new Date(arr.getFullYear(), arr.getMonth(), arr.getDate());
    
    // Calculate difference in days
    const diffTime = arrDay - depDay;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };
  
  const formatDuration = (isoDuration) => {
    if (!isoDuration) return '';
    const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!match) return isoDuration;
    const hours = match[1] || '0';
    const minutes = match[2] || '0';
    return `${hours}h ${minutes}m`;
  };

  const formatCompactDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getFlightNumber = (segments) => {
    if (!segments || !segments[0]) return null;
    const seg = segments[0];
    const code = seg.marketing_carrier?.iata_code || seg.marketing_carrier_code || '';
    const num = seg.marketing_carrier_flight_number || seg.marketing_flight_number || '';
    return `${code}${num}`.trim();
  };

  const getAirportName = (location) => {
    if (!location) return '';
    if (typeof location === 'string') return location;
    // Full airport name
    const name = location.name || location.city_name || location.city?.name || '';
    const code = location.iata_code || location.iata_city_code || '';
    return name ? `${name} (${code})` : code;
  };

  const getAirportCode = (location) => {
    if (!location) return '';
    if (typeof location === 'string') return location;
    return location.iata_code || location.iata_city_code || '';
  };

  // Helper to get data from either raw Duffel format (slices) or formatted format
  const getFlightData = (flight) => {
    if (!flight) return null;
    
    // If it's a raw slice from Duffel API
    if (flight.segments && Array.isArray(flight.segments) && flight.segments.length > 0) {
      const firstSeg = flight.segments[0];
      const lastSeg = flight.segments[flight.segments.length - 1];
      
      // Get baggage - could be on flight.baggage_allowances or flight.passengers[0].baggage_allowances
      let baggageAllowances = flight.baggage_allowances || [];
      if (!baggageAllowances.length && flight.passengers && flight.passengers.length > 0) {
        baggageAllowances = flight.passengers[0].baggage_allowances || [];
      }
      
      return {
        airlineLogo: firstSeg.marketing_carrier?.logo_symbol_url || null,
        airlineName: firstSeg.marketing_carrier?.name || firstSeg.operating_carrier?.name || 'Duffel Airways',
        cabinClass: firstSeg.passengers?.[0]?.cabin_class_marketing_name || firstSeg.passengers?.[0]?.cabin_class || 'Economy',
        departureTime: firstSeg.departing_at,
        arrivalTime: lastSeg.arriving_at,
        departureAirport: getAirportCode(firstSeg.origin),
        arrivalAirport: getAirportCode(lastSeg.destination),
        departureAirportFull: getAirportName(firstSeg.origin),
        arrivalAirportFull: getAirportName(lastSeg.destination),
        departureTerminal: firstSeg.origin_terminal || null,
        arrivalTerminal: lastSeg.destination_terminal || null,
        duration: flight.duration,
        stops: flight.segments.length - 1,
        flightNumber: getFlightNumber(flight.segments),
        baggageAllowances: baggageAllowances
      };
    }
    
    // If it's formatted data from our backend
    return {
      airlineLogo: flight.airline_logo,
      airlineName: flight.airline || 'Unknown Airline',
      cabinClass: flight.cabin_class || 'Economy',
      departureTime: flight.departure?.time || flight.departure_time,
      arrivalTime: flight.arrival?.time || flight.arrival_time,
      departureAirport: flight.departure?.airport || flight.origin,
      arrivalAirport: flight.arrival?.airport || flight.destination,
      departureAirportFull: flight.departure?.city ? `${flight.departure.city} (${flight.departure.airport})` : (flight.departure?.airport || flight.origin),
      arrivalAirportFull: flight.arrival?.city ? `${flight.arrival.city} (${flight.arrival.airport})` : (flight.arrival?.airport || flight.destination),
      departureTerminal: flight.departure?.terminal,
      arrivalTerminal: flight.arrival?.terminal,
      duration: flight.duration,
      stops: flight.stops || 0,
      flightNumber: getFlightNumber(flight.segments),
      baggageAllowances: flight.baggage_allowances || []
    };
  };

  const outbound = getFlightData(outboundFlight);
  const inbound = inboundFlight ? getFlightData(inboundFlight) : null;

  if (!outbound) return null;

  const renderFlightCard = (flight, isReturn = false) => {
    const dayDiff = getDayDifference(flight.departureTime, flight.arrivalTime);
    
    return (
      <Box sx={{ mb: isReturn ? 0 : 2 }}>
        {/* Compact Header Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Airline Logo */}
            {flight.airlineLogo && (
              <Box sx={{ width: 48, height: 48, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.75 }}>
                <img 
                  src={flight.airlineLogo} 
                  alt={flight.airlineName}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </Box>
            )}
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
                {formatCompactDate(flight.departureTime)}{'  '}
                {formatTime(flight.departureTime)} - {formatTime(flight.arrivalTime)}
                {dayDiff > 0 && <sup>+{dayDiff}</sup>}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {flight.cabinClass} · {flight.airlineName}
              </Typography>
            </Box>
          </Box>
          
          {/* Right side info */}
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatDuration(flight.duration)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {flight.departureAirport} – {flight.arrivalAirport}
            </Typography>
          </Box>
        </Box>

        {/* Compact Timeline */}
        <Box sx={{ pl: 1 }}>
          {/* Departure */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #666', mr: 2, mt: 0.5, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatCompactDate(flight.departureTime)}, {formatTime(flight.departureTime)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Depart from {flight.departureAirportFull}
                {flight.departureTerminal && `, Terminal ${flight.departureTerminal}`}
              </Typography>
            </Box>
          </Box>

          {/* Flight duration */}
          <Box sx={{ borderLeft: '2px solid #e0e0e0', ml: '4px', pl: 3, py: 0.5, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Flight duration: {formatDuration(flight.duration)}
            </Typography>
          </Box>

          {/* Arrival */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #666', mr: 2, mt: 0.5, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatCompactDate(flight.arrivalTime)}, {formatTime(flight.arrivalTime)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Arrive at {flight.arrivalAirportFull}
                {flight.arrivalTerminal && `, Terminal ${flight.arrivalTerminal}`}
              </Typography>
            </Box>
          </Box>

          {/* Tags Row with inline icons */}
          <Typography variant="body2" color="text.secondary" component="div" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>{flight.cabinClass}</span>
            <span>{flight.airlineName}</span>
            {flight.flightNumber && <span>{flight.flightNumber}</span>}
            {flight.baggageAllowances && flight.baggageAllowances.length > 0 && (
              <>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Luggage sx={{ fontSize: 16 }} />
                  {flight.baggageAllowances[0].quantity || 1} carry-on bag
                </span>
                {flight.baggageAllowances.find(b => b.type === 'checked') && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <Luggage sx={{ fontSize: 16 }} />
                    {flight.baggageAllowances.find(b => b.type === 'checked').quantity} checked bag
                  </span>
                )}
              </>
            )}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 0, mb: 3, bgcolor: 'transparent', boxShadow: 'none' }}>
      {/* Price Header (if showPrice) */}
      {showPrice && totalPrice && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {outbound.airlineLogo && (
              <Box sx={{ width: 48, height: 48, bgcolor: '#5B21B6', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.75 }}>
                <img 
                  src={outbound.airlineLogo} 
                  alt={outbound.airlineName}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', filter: 'brightness(0) invert(1)' }}
                />
              </Box>
            )}
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.25 }}>
                {outbound.airlineName} — {outbound.departureAirport} → {outbound.arrivalAirport}
                {inbound && ` → ${inbound.arrivalAirport}`}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#8B5CF6' }}>
                {totalPrice}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Selected Flights Section */}
      {!showPrice && (
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Selected flights
        </Typography>
      )}

      {/* Outbound Flight */}
      {renderFlightCard(outbound, false)}

      {/* Inbound Flight */}
      {inbound && renderFlightCard(inbound, true)}
    </Paper>
  );
}

export default FlightSummaryCard;
